"use client"

/**
 * The AI Spotted table cell. One renderer, every table.
 *
 * The bank, the assessment paper and the Add questions pool each drew this
 * column their own way — or, in the pool's case, did not draw it at all —
 * and each reached for the tag differently: the bank through
 * `spottedForLibraryItem`, the paper straight off
 * `questionCatalogById(row.id).aiSpotted`. Same question, three answers,
 * which is the bug the author kept hitting. Every table now hands this
 * component a `LeoSpottedSubject` and renders whatever `readLeoSpotted`
 * says.
 *
 * The three states it can show, and they are not interchangeable:
 *
 * - a tag (Needs fixing / Worth a look / Weak performance)
 * - **All good**, for a question whose findings the author has cleared
 * - **Not checked**, for one Leo has never read
 *
 * The last two are the pair that matters. The PRD draws the unanalysed
 * state as a bare dash, which is honest but unreadable in a column of
 * them: a dash says nothing about whether the question is fine or simply
 * has not been looked at, and those are opposite facts. Naming the state
 * costs one quiet neutral chip and removes the guess. It stays
 * deliberately low contrast so a column of unanalysed rows does not
 * compete with the rows that actually need attention (PRD, alert
 * fatigue).
 *
 * Analysis runs on import and on save-as-draft, so most of the standing
 * bank genuinely has not been read.
 */

import { AiSpottedMark } from "@/components/ai-spotted-mark"
import { EmptyCell, StatusCell } from "@/components/data-views"
import { LeoSpottedTablePopover } from "@/components/question-quality/leo-spotted-table-popover"
import { aiSpottedBrief } from "@/lib/library-ai-spotted"
import {
  readLeoSpotted,
  type LeoSpottedSubject,
} from "@/lib/question-quality/leo-spotted-source"

export function LeoSpottedTableCell({
  subject,
  /** True while Leo is mid-apply on this row; the cell stays quiet. */
  busy,
  /** Opens the sheet when the author runs an action from a Leo spotted card. */
  onOpenSheet,
  stopRowClick = true,
}: {
  subject: LeoSpottedSubject
  busy?: boolean
  onOpenSheet?: () => void
  stopRowClick?: boolean
}) {
  const reading = readLeoSpotted(subject)
  if (busy) return <EmptyCell label="—" />
  if (!reading.tag) {
    return (
      <span className="inline-flex max-w-full items-center gap-1">
        {reading.resolvedCount > 0 ? (
          <StatusCell label="All good" tone="success" icon="fa-circle-check" />
        ) : (
          <StatusCell label="Not checked" tone="neutral" icon="fa-circle-dashed" />
        )}
        <LeoSpottedTablePopover
          subject={subject}
          stopRowClick={stopRowClick}
          onOpenSheet={onOpenSheet}
        />
      </span>
    )
  }
  const brief = aiSpottedBrief({
    id: subject.id,
    tag: reading.tag,
    title: subject.title,
  })
  return (
    <AiSpottedMark
      subject={subject}
      tag={reading.tag}
      why={reading.why}
      ask={brief.ask}
      stopRowClick={stopRowClick}
      onOpenSheet={onOpenSheet}
    />
  )
}
